import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  wipItems: [],
  trackers: [],
  currentTracker: null,
  trackerDetails: [],
  comments: [],
  statusRollup: {
    onTrackCount: 0,
    atRiskCount: 0,
    overdueCount: 0,
    completionPercent: 0,
  },
  pagination: {
    total: 0,
    limit: 10,
    offset: 0,
    hasMore: false,
  },
  lockedState: {
    state: false,
    lockedByUserId: null,
    lockedByUserName: null,
    lockedAt: null,
  },
  selectedTab: "batch",
  loading: false,
  uploading: false,
  saving: false,
  error: null,
  uploadSuccess: null,
  // WIP Reports (replaces batchUpdateIds)
  wipReports: [],
  selectedReportId: null, // wipId (report _id)
  batchItems: [],
  batchPagination: {
    total: 0,
    limit: 100,
    offset: 0,
    hasMore: false,
  },
  loadingBatch: false,
  // Past-due items
  pastDueItems: [],
  loadingPastDue: false,
};

const wipSlice = createSlice({
  name: "wip",
  initialState,
  reducers: {
    getWipItems: (state, action) => {
      state.loading = true;
      state.error = null;
    },
    setWipItems: (state, action) => {
      console.log(
        "WIP Reducer: Setting wipItems with payload:",
        action.payload
      );
      console.log("WIP Reducer: Number of items:", action.payload?.length || 0);
      state.wipItems = action.payload;
      state.loading = false;
      state.error = null;
    },
    setWipStatusRollup: (state, action) => {
      state.statusRollup = action.payload;
    },
    setWipPagination: (state, action) => {
      state.pagination = action.payload;
    },
    setWipLoading: (state, action) => {
      state.loading = action.payload;
    },
    setWipError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    setSelectedTab: (state, action) => {
      state.selectedTab = action.payload;
    },
    // Upload Excel
    uploadWipExcel: (state, action) => {
      state.uploading = true;
      state.error = null;
      state.uploadSuccess = null;
    },
    setUploadWipExcelSuccess: (state, action) => {
      state.uploading = false;
      state.uploadSuccess = action.payload;
      state.error = null;
    },
    setUploadWipExcelError: (state, action) => {
      state.uploading = false;
      state.error = action.payload;
      state.uploadSuccess = null;
    },
    // Get Trackers
    getWipTrackers: (state, action) => {
      state.loading = true;
      state.error = null;
    },
    setWipTrackers: (state, action) => {
      state.trackers = action.payload.trackers || [];
      state.pagination = action.payload.pagination || state.pagination;
      state.loading = false;
      state.error = null;
    },
    // Get Tracker Details
    getWipTrackerDetails: (state, action) => {
      state.loading = true;
      state.error = null;
    },
    setWipTrackerDetails: (state, action) => {
      console.log("=== Reducer: setWipTrackerDetails ===");
      console.log("Payload:", action.payload);
      console.log("Details length:", action.payload.details?.length);
      console.log("First detail:", action.payload.details?.[0]);
      state.trackerDetails = action.payload.details || [];
      state.currentTracker = action.payload.tracker || null;
      state.loading = false;
      state.error = null;
      console.log(
        "State after update - trackerDetails length:",
        state.trackerDetails.length
      );
    },
    // Bulk Update Details
    bulkUpdateWipDetails: (state, action) => {
      state.saving = true;
      state.error = null;
    },
    setBulkUpdateWipDetailsSuccess: (state, action) => {
      state.saving = false;
      state.error = null;
    },
    setBulkUpdateWipDetailsError: (state, action) => {
      state.saving = false;
      state.error = action.payload;
    },
    // Get Comments
    getWipComments: (state, action) => {
      state.loading = true;
      state.error = null;
    },
    setWipComments: (state, action) => {
      state.comments = action.payload;
      state.loading = false;
      state.error = null;
    },
    // Add Comment
    addWipComment: (state, action) => {
      state.loading = true;
      state.error = null;
    },
    setAddWipCommentSuccess: (state, action) => {
      state.loading = false;
      state.error = null;
    },
    // Resolve Comment
    resolveWipComment: (state, action) => {
      state.loading = true;
      state.error = null;
    },
    setResolveWipCommentSuccess: (state, action) => {
      state.loading = false;
      state.error = null;
    },
    // Delete Tracker
    deleteWipTracker: (state, action) => {
      state.loading = true;
      state.error = null;
    },
    setDeleteWipTrackerSuccess: (state, action) => {
      state.loading = false;
      state.error = null;
    },
    clearWipData: (state) => {
      state.wipItems = [];
      state.statusRollup = {
        onTrackCount: 0,
        atRiskCount: 0,
        overdueCount: 0,
        completionPercent: 0,
      };
      state.pagination = {
        total: 0,
        limit: 10,
        offset: 0,
        hasMore: false,
      };
      state.loading = false;
      state.error = null;
    },
    // WIP Reports Actions (replaces batch update IDs)
    getWipReports: (state, action) => {
      state.loadingBatch = true;
      state.error = null;
    },
    setWipReports: (state, action) => {
      state.wipReports = action.payload;
      state.loadingBatch = false;
      state.error = null;
    },
    getWipReportById: (state, action) => {
      state.loadingBatch = true;
      state.error = null;
    },
    setWipReportById: (state, action) => {
      const report = action.payload;
      // Add or update the report in the wipReports array
      const existingIndex = state.wipReports.findIndex(
        (r) => r._id === report._id
      );
      if (existingIndex >= 0) {
        state.wipReports[existingIndex] = report;
      } else {
        state.wipReports.push(report);
      }
      state.loadingBatch = false;
      state.error = null;
    },
    setSelectedReportId: (state, action) => {
      state.selectedReportId = action.payload;
    },
    // Backward compatibility aliases
    getBatchUpdateIds: (state, action) => {
      state.loadingBatch = true;
      state.error = null;
    },
    setBatchUpdateIds: (state, action) => {
      // Convert batchUpdateIds array to reports format (for backward compatibility)
      state.wipReports = action.payload.map((id) => ({ _id: id }));
      state.loadingBatch = false;
      state.error = null;
    },
    setSelectedBatchId: (state, action) => {
      state.selectedReportId = action.payload;
    },
    getWipItemsByBatch: (state, action) => {
      state.loadingBatch = true;
      state.error = null;
    },
    setWipItemsByBatch: (state, action) => {
      state.batchItems = action.payload.items || action.payload;
      state.lockedState = action.payload.lockedState || state.lockedState;
      state.batchPagination =
        action.payload.pagination || state.batchPagination;
      state.loadingBatch = false;
      state.error = null;
    },
    setLockedState: (state, action) => {
      state.lockedState = action.payload;
    },
    setBatchError: (state, action) => {
      state.error = action.payload;
      state.loadingBatch = false;
    },
    // Past-due actions
    getPastDueItems: (state, action) => {
      state.loadingPastDue = true;
      state.error = null;
    },
    setPastDueItems: (state, action) => {
      state.pastDueItems = action.payload || [];
      state.loadingPastDue = false;
      state.error = null;
    },
    setPastDueError: (state, action) => {
      state.error = action.payload;
      state.loadingPastDue = false;
    },
  },
});

export const {
  getWipItems,
  setWipItems,
  setWipStatusRollup,
  setWipPagination,
  setWipLoading,
  setWipError,
  setSelectedTab,
  uploadWipExcel,
  setUploadWipExcelSuccess,
  setUploadWipExcelError,
  getWipTrackers,
  setWipTrackers,
  getWipTrackerDetails,
  setWipTrackerDetails,
  bulkUpdateWipDetails,
  setBulkUpdateWipDetailsSuccess,
  setBulkUpdateWipDetailsError,
  getWipComments,
  setWipComments,
  addWipComment,
  setAddWipCommentSuccess,
  resolveWipComment,
  setResolveWipCommentSuccess,
  deleteWipTracker,
  setDeleteWipTrackerSuccess,
  clearWipData,
  // WIP Reports actions
  getWipReports,
  setWipReports,
  getWipReportById,
  setWipReportById,
  setSelectedReportId,
  setLockedState,
  // Batch update actions (backward compatibility)
  getBatchUpdateIds,
  setBatchUpdateIds,
  setSelectedBatchId,
  getWipItemsByBatch,
  setWipItemsByBatch,
  setBatchError,
  // Past-due actions
  getPastDueItems,
  setPastDueItems,
  setPastDueError,
} = wipSlice.actions;

export default wipSlice.reducer;
